import { BOT_FILTER_KEY } from "./bot-presets";
import { isUrlFilterMultiEntry, type UrlActiveFilters, type UrlFilterAnyMode, type UrlFilterMode } from "./url-active-filters";

/**
 * The Clicks filter bar's model: one filter per field - include or exclude a
 * list of values, or (contains / not_contains) a list of substrings matched in
 * any case. Fields AND together, a field's values OR (clicks_filter_sql).
 * Pure functions over the URL's `filters`, so the bar, the right-click menu and
 * saved views share one format.
 */

export interface FieldFilter {
  mode: UrlFilterAnyMode;
  values: string[];
}

/**
 * A link holds the filters, and a link past ~2 MB breaks; 500 values a field
 * keeps a full one well under that while covering "my 300 domains". It limits
 * what can be ADDED (a tick, a row, Keep only); a filter that already holds
 * more (an older link or saved view) is kept whole, never cut.
 */
export const MAX_FILTER_VALUES = 500;

/** Text modes: the values are pieces of text, not values of the field. */
export const isTextMode = (mode: UrlFilterAnyMode): boolean => mode === "contains" || mode === "not_contains";
/** The modes that leave matching rows out - drawn red. */
export const isNegativeMode = (mode: UrlFilterAnyMode): boolean => mode === "exclude" || mode === "not_contains";
/** How a chip says the mode: "is", "is not", "contains", "doesn't contain". */
export const modeWord = (mode: UrlFilterAnyMode): string =>
  mode === "exclude" ? "is not" : mode === "contains" ? "contains" : mode === "not_contains" ? "doesn't contain" : "is";

/** At most this many pieces of text in one "contains", each at most this long. */
export const MAX_TEXT_PATTERNS = 20;
export const MAX_PATTERN_LENGTH = 200;

/**
 * One more piece of text for a "contains": trimmed, cut to MAX_PATTERN_LENGTH,
 * a repeat in another case is the same piece (the match ignores case). The
 * list as it was when there is nothing to add or it is full.
 */
export const addPattern = (patterns: string[], text: string): string[] => {
  const t = text.trim().slice(0, MAX_PATTERN_LENGTH);
  if (!t || patterns.length >= MAX_TEXT_PATTERNS || patterns.some((p) => p.toLowerCase() === t.toLowerCase())) return patterns;
  return [...patterns, t];
};

/** The field's filter, or null when it has none. */
export const fieldFilterOf = (filters: UrlActiveFilters, key: string): FieldFilter | null => {
  const entry = filters[key];
  if (!entry || !isUrlFilterMultiEntry(entry) || entry.values.length === 0) return null;
  return { mode: entry.mode, values: entry.values };
};

/**
 * The filters with this field set. An empty or null filter removes the field:
 * an empty exclude used to stay in the link as an "(empty)" pill and reload
 * the table for nothing. Repeats go; nothing is cut (MAX_FILTER_VALUES).
 */
export const withFieldFilter = (
  filters: UrlActiveFilters,
  key: string,
  filter: FieldFilter | null,
): UrlActiveFilters => {
  const next = { ...filters };
  delete next[key];
  if (filter && filter.values.length) {
    next[key] = { mode: filter.mode, values: [...new Set(filter.values)] };
  }
  return next;
};

/** Fields with a value filter, in the order they were added; bots are a control of their own. */
export const activeFieldKeys = (filters: UrlActiveFilters): string[] =>
  Object.keys(filters).filter((k) => k !== BOT_FILTER_KEY && fieldFilterOf(filters, k) !== null);

/** Every value filter gone; the bot preset stays. */
export const withoutFieldFilters = (filters: UrlActiveFilters): UrlActiveFilters => {
  const next: UrlActiveFilters = {};
  if (filters[BOT_FILTER_KEY]) next[BOT_FILTER_KEY] = filters[BOT_FILTER_KEY];
  return next;
};

/** The values with `value` switched on or off; off the end once the field is full. */
export const toggleValue = (values: string[], value: string): string[] =>
  values.includes(value)
    ? values.filter((v) => v !== value)
    : values.length >= MAX_FILTER_VALUES
      ? values
      : [...values, value];

/** "DE, PL" / "casino-a.de +2": what a chip says about its values. */
export const describeValues = (values: string[], shown = 2, label: (v: string) => string = (v) => v): string =>
  values.length <= shown
    ? values.map(label).join(", ")
    : `${values.slice(0, shown - 1).map(label).join(", ")} +${values.length - (shown - 1)}`;

/** A filter's values as a chip says them - pieces of text in quotes. */
export const describeFilterValues = (filter: FieldFilter, shown = 2, valueLabel?: (v: string) => string): string =>
  isTextMode(filter.mode) ? describeValues(filter.values, shown, (v) => `“${v}”`) : describeValues(filter.values, shown, valueLabel);

/** What a chip reads, for the eye and for a screen reader: "Country is DE, PL", "Domain contains “casino”". */
export const describeFieldFilter = (label: string, filter: FieldFilter, valueLabel?: (v: string) => string): string =>
  `${label} ${modeWord(filter.mode)} ${describeFilterValues(filter, 2, valueLabel)}`;

/** Whether two filters say the same thing (order of values aside). */
export const sameFieldFilter = (a: FieldFilter | null, b: FieldFilter | null): boolean =>
  a === b ||
  (!!a && !!b && a.mode === b.mode && a.values.length === b.values.length && a.values.every((v) => b.values.includes(v)));

// ─── one value at a time: the table's own buttons and menus ───

/** "Filter by this value" / "Exclude this value": the field becomes just this. */
export const filterByValue = (filters: UrlActiveFilters, key: string, value: string, mode: UrlFilterMode): UrlActiveFilters =>
  withFieldFilter(filters, key, { mode, values: [value] });

/**
 * "Add to filter": one more value, the field's mode kept - "is one of A, B, C"
 * click by click. Null when the field is full: the value would be cut off
 * while the toast said "Added".
 */
export const addValueToFilter = (filters: UrlActiveFilters, key: string, value: string): UrlActiveFilters | null => {
  const cur = fieldFilterOf(filters, key);
  // A "contains" holds pieces of text: a value added there would quietly match
  // every value containing it. Its chip is the place to change it.
  if (cur && isTextMode(cur.mode)) return null;
  const values = cur?.values ?? [];
  if (!values.includes(value) && values.length >= MAX_FILTER_VALUES) return null;
  return withFieldFilter(filters, key, { mode: cur?.mode ?? "include", values: [...values, value] });
};

/** Checked rows → "Keep only": the field is exactly them. Null past the cap. */
export const keepOnlyValues = (filters: UrlActiveFilters, key: string, values: string[]): UrlActiveFilters | null =>
  values.length && new Set(values).size <= MAX_FILTER_VALUES
    ? withFieldFilter(filters, key, { mode: "include", values })
    : null;

/**
 * Checked rows → "Exclude": out of an "is" filter, or onto an "is not" one.
 * Null when nothing would be left of an "is" filter, or past the cap - the
 * button says so instead of silently filtering something else.
 */
export const excludeValues = (filters: UrlActiveFilters, key: string, values: string[]): UrlActiveFilters | null => {
  if (!values.length) return null;
  const cur = fieldFilterOf(filters, key);
  // Out of a "contains" there is nothing to take; onto it, the values would
  // become pieces of text (see addValueToFilter).
  if (cur && isTextMode(cur.mode)) return null;
  if (cur?.mode === "include") {
    const rest = cur.values.filter((v) => !values.includes(v));
    return rest.length ? withFieldFilter(filters, key, { mode: "include", values: rest }) : null;
  }
  const all = [...new Set([...(cur?.values ?? []), ...values])];
  return all.length <= MAX_FILTER_VALUES ? withFieldFilter(filters, key, { mode: "exclude", values: all }) : null;
};

// ─── marks (20261030183128): "mark:<field>" holds marks, not values ───

const MARK_PREFIX = "mark:";
export const markFilterKey = (field: string): string => MARK_PREFIX + field;
export const isMarkKey = (key: string): boolean => key.startsWith(MARK_PREFIX);
/** The field a mark filter is about: "mark:domain" → "domain". */
export const markFieldOf = (key: string): string => key.slice(MARK_PREFIX.length);
